'use client';

import { useEffect, useState } from 'react';
import Header from '@/app/components/Header';
import Footer from '@/app/components/Footer';
import Link from 'next/link';
import CustomRequestForm from '@/app/components/CustomRequestForm';

function CustomRequestPageContent() {
  const [productContext, setProductContext] = useState<{ productName?: string; productUrl?: string }>({});
  const [pageContent, setPageContent] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const readProductContext = () => {
      // New links use #product=... so Google never discovers a different URL
      // for every product. Keep query-string support for old bookmarks and
      // links already shared with customers.
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const params = hashParams.size > 0 ? hashParams : new URLSearchParams(window.location.search);
      setProductContext({
        productName: params.get('product') || undefined,
        productUrl: params.get('productUrl') || undefined,
      });
    };

    readProductContext();
    window.addEventListener('hashchange', readProductContext);
    return () => window.removeEventListener('hashchange', readProductContext);
  }, []);

  useEffect(() => {
    fetch('/api/pages/custom-jewelry-request')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.success && data.data) {
          setPageContent(data.data.content || '');
        }
      })
      .catch((err) => console.error('Error fetching custom request page description:', err));
  }, []);

  return (
    <>
      <Header />
      <main className="w-full min-h-screen bg-gray-50 pb-24 flex flex-col justify-between">

        <div className="w-full mb-12">
          {/* Top Header Row matching screenshot */}
          <div className="w-full bg-white border-b border-gray-200 py-6 mb-10 shadow-sm px-6 md:px-12">
            <div className="w-full flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900 font-serif">Custom Jewelry Request</h1>
              <div className="text-[11px] text-gray-500 flex items-center gap-1">
                <Link href="/" className="hover:text-[var(--orange-hover)]">Home</Link>
                <span>/</span>
                <span className="font-semibold text-gray-800">"Custom Jewelry Request"</span>
              </div>
            </div>
          </div>

          {/* Main Content Form - Full Width Padding */}
          <div className="w-full px-6 md:px-12">
            <CustomRequestForm productName={productContext.productName} productUrl={productContext.productUrl} />
          </div>
        </div>

        {/* Dynamic Rich-Text Description content with Read More toggle at bottom of page */}
        {pageContent && (
          <div className="w-full bg-[#fdfbf7] border-t border-gray-150 py-12 px-6 md:px-12 mt-auto">
            <div className="max-w-7xl mx-auto">
              <div className={`relative overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-none' : 'max-h-[220px]'}`}>
                <div
                  className="text-xs md:text-sm text-gray-600 leading-relaxed prose prose-amber max-w-none
                             [&>p]:mb-4 [&>p]:leading-relaxed [&>a]:text-amber-800 [&>a]:underline hover:[&>a]:text-amber-900
                             [&>h1]:text-lg [&>h1]:md:text-xl [&>h1]:font-serif [&>h1]:font-bold [&>h1]:text-gray-900 [&>h1]:mb-4 [&>h1]:mt-6
                             [&>h2]:text-base [&>h2]:md:text-lg [&>h2]:font-serif [&>h2]:font-bold [&>h2]:text-gray-800 [&>h2]:mt-6 [&>h2]:mb-3
                             [&>h3]:text-sm [&>h3]:md:text-base [&>h3]:font-serif [&>h3]:font-bold [&>h3]:text-gray-800 [&>h3]:mt-4 [&>h3]:mb-2"
                  dangerouslySetInnerHTML={{ __html: pageContent }}
                />
                {!isExpanded && (
                  <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#fdfbf7] via-[#fdfbf7]/80 to-transparent pointer-events-none" />
                )}
              </div>

              <div className="text-center mt-6">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="px-6 py-2 border border-amber-800/40 rounded-full text-xs font-bold text-amber-800 hover:bg-amber-50 hover:border-amber-800 transition duration-300 cursor-pointer"
                >
                  {isExpanded ? '- Read Less' : '+ Read More'}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
      <Footer />
    </>
  );
}

export default function CustomRequestPageClient() {
  return <CustomRequestPageContent />;
}
